/**
 * MPCI Color constants — MIUI theme palette ONLY.
 * NO hardcoded hex. All colors resolve via theme.palette at runtime.
 *
 * Usage:
 *   import { getStatusColor, getChartColors } from '../Common/constants/colors'
 *   const colors = getStatusColor(theme, 'SUBMITTED_TO_NAIC')
 */

/**
 * Status color mappings — resolves via theme.palette
 * @param {object} theme - MUI theme
 * @param {string} status - status code (DRAFT, SUBMITTED_TO_NAIC, etc.)
 * @returns {{ bg: string, text: string, border: string }}
 */
export function getStatusColor(theme, status) {
  const p = theme.palette
  const fallback = {
    bg: p.background.sectionOddBg,
    text: p.text.secondary,
    border: p.divider
  }

  const map = {
    DRAFT: {
      bg: p.background.sectionOddBg,
      text: p.text.secondary,
      border: p.divider
    },
    SUBMITTED_TO_NAIC: {
      bg: p.info.light,
      text: p.info.main,
      border: p.info.main
    },
    SUCCESSFUL_ACK: {
      bg: p.success.light,
      text: p.success.dark,
      border: p.success.main
    },
    FAILED_ACK: {
      bg: p.warning.light,
      text: p.warning.dark,
      border: p.warning.main
    },
    ASSESSMENT_COMPLETE: {
      bg: p.success.light,
      text: p.success.main,
      border: p.success.main
    },
    ASSESSMENT_COMPLETE_WARNING: {
      bg: p.warning.light,
      text: p.warning.dark,
      border: p.warning.main
    },
    DO_NOT_LOAD: {
      bg: p.error.light,
      text: p.error.main,
      border: p.error.main
    },
    FILING_EXPIRY: {
      bg: p.background.sectionOddBg,
      text: p.text.disabled,
      border: p.divider
    },
    REQUEST_FOR_INFO: {
      bg: p.secondary.light,
      text: p.secondary.main,
      border: p.secondary.main
    },
    CANCELLATION_SUCCESS: {
      bg: p.success.light,
      text: p.success.main,
      border: p.success.main
    },
    CANCELLATION_FAILED: {
      bg: p.error.light,
      text: p.error.dark,
      border: p.error.main
    },
    ERROR_IN_SUBMISSION: {
      bg: p.error.light,
      text: p.error.main,
      border: p.error.main
    }
  }

  return map[status] || fallback
}

/**
 * Chart/donut segment colors — keyed by BE status code.
 * Every status gets a visually distinct color using the full MUI palette.
 * NO hardcoded hex — all resolved via theme.palette at runtime.
 *
 * @param {object} theme - MUI theme
 * @returns {object} statusCode → color string
 */
export function getChartColors(theme) {
  const p = theme.palette
  return {
    // ── Submission states ──────────────────────────────────────────────────
    SUBM:               p.info.main,           // blue       — Submitted
    SUBM_ERR:           p.error.main,          // red        — Error in Submission

    // ── Acknowledgement states ─────────────────────────────────────────────
    CONTRL_ACK:         p.success.main,        // green      — Successful Acknowledgment
    CONTRL_NACK:        p.warning.dark,        // amber-dark — Controller NACK

    // ── Assessment states ──────────────────────────────────────────────────
    ACT:                p.success.dark,        // dark green — Assessment Complete
    ACW:                p.warning.main,        // amber      — Assessment Complete with Warning

    // ── Negative / terminal states ─────────────────────────────────────────
    DNL:                p.error.dark,          // dark red   — Do Not Load
    EXP:                p.text.secondary,      // grey       — Filing Expiry

    // ── Action required ────────────────────────────────────────────────────
    PSF:                p.secondary.light,     // light purple — Pending Sub-filing
    RFI:                p.secondary.main,      // purple     — Request for Information

    // ── Cancellation states ────────────────────────────────────────────────
    CANCEL_CONTRL_ACK:  p.info.dark,           // dark blue  — Cancellation Successful
    CANCEL_CONTRL_NACK: p.error.light          // light red  — Cancellation Failed
  }
}

/**
 * Trend arrow color helper
 * @param {object} theme - MUI theme
 * @param {number} trend - positive/negative/zero
 * @returns {string} theme color
 */
export function getTrendColor(theme, trend) {
  const p = theme.palette
  if (trend > 0) return p.success.main
  if (trend < 0) return p.error.main
  return p.text.disabled
}

/**
 * Upload status color helper
 * @param {object} theme - MUI theme
 * @param {string} status - COMPLETED/PROCESSING/FAILED/PENDING
 * @returns {string} theme color
 */
export function getUploadStatusColor(theme, status) {
  const p = theme.palette
  const map = {
    COMPLETED: p.success.main,
    PROCESSING: p.warning.main,
    FAILED: p.error.main,
    PENDING: p.text.disabled
  }
  return map[status] || p.text.primary
}
